/**
 * Settings > Claims > Approvals - the LOA ladders each claim type runs.
 *
 * Read-only: the levels, amounts and roles come from the tenant's approval
 * config. The one setting here is each claim type's approval order - every
 * level up to the amount signs in turn, or only the level the amount falls
 * in. People are managed on the roles each level names (Settings > Roles).
 */

import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useFormattingContext } from '@/shared/lib/formatting'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useUpdateApprovalOrder,
  type ApprovalLadderDimension,
  type ApprovalLadderTier,
  type ApprovalLadderWorkflow,
  type ApprovalOrder,
} from '@/admin/hooks/use-approval-policy'

function formatAmount(
  value: string | number | null,
  locale: string,
  currency: string,
): string | null {
  if (value === null || value === undefined || value === '') return null
  const amount = Number(value)
  if (Number.isNaN(amount)) return null
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** Product lines whose ladders are identical, shown once. */
function groupedLadders(dimension: ApprovalLadderDimension) {
  const groups = new Map<string, { names: string[]; tiers: ApprovalLadderTier[] }>()
  for (const ladder of dimension.ladders) {
    const signature = JSON.stringify(ladder.tiers)
    const group = groups.get(signature)
    if (group) group.names.push(ladder.matrix_key)
    else groups.set(signature, { names: [ladder.matrix_key], tiers: ladder.tiers })
  }
  return [...groups.values()]
}

function humanise(key: string): string {
  return key ? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ') : ''
}

function LadderRows({ tiers }: { tiers: ApprovalLadderTier[] }) {
  const { t } = useTranslation()
  const { locale, currency } = useFormattingContext()
  return (
    <div className="flex flex-col gap-1.5">
      {tiers.map((tier) => {
        const min = formatAmount(tier.min_amount, locale, currency)
        const max =
          formatAmount(tier.max_amount, locale, currency) ??
          t('claims.approvals.ladders.noCap', 'No cap')
        return (
          <div
            key={tier.order}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border-[0.5px] border-[var(--rule)] px-3 py-2"
          >
            <div className="flex flex-col">
              <span className="text-[13px] text-[var(--ink)]">{tier.role_label}</span>
              <span className="text-[11px] text-[var(--ink-3)]">
                {tier.approver_role ||
                  tier.approver_name ||
                  t('claims.approvals.ladders.anyApprover', 'Any LOA approver')}
              </span>
            </div>
            <span className="text-[12px] text-[var(--ink-2)]">
              {tier.band_label || `${min ?? ''} - ${max}`}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function WorkflowLadders({
  workflow,
  canEdit,
}: {
  workflow: ApprovalLadderWorkflow
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const update = useUpdateApprovalOrder()

  const onOrderChange = (value: string) => {
    update.mutate(
      { workflowKey: workflow.workflow_key, signOffMode: value as ApprovalOrder },
      {
        onSuccess: () =>
          toast.success(t('claims.approvals.ladders.saved', 'Approval order saved.')),
        onError: (err) =>
          toastApiError(
            err,
            t('claims.approvals.ladders.saveError', 'Failed to save the approval order'),
          ),
      },
    )
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[14px] font-medium text-[var(--ink)]">{workflow.label}</span>
          <span className="text-[11px] text-[var(--ink-3)]">
            {t(
              'claims.approvals.ladders.orderHint',
              'Applies from the next signature, including claims already waiting.',
            )}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
            {t('claims.approvals.ladders.order', 'Approval order')}
          </span>
          <Select
            value={workflow.sign_off_mode}
            onValueChange={onOrderChange}
            disabled={!canEdit || update.isPending}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cumulative">
                {t('claims.approvals.ladders.orderCumulative', 'Every level up to the amount')}
              </SelectItem>
              <SelectItem value="band">
                {t('claims.approvals.ladders.orderBand', 'Only the level the amount falls in')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {workflow.dimensions.map((dimension) => (
        <div key={dimension.key} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{dimension.label}</Badge>
            {dimension.amount_label && (
              <span className="text-[11px] text-[var(--ink-3)]">
                {t('claims.approvals.ladders.drivenBy', 'Driven by {{amount}}', {
                  amount: dimension.amount_label,
                })}
              </span>
            )}
          </div>
          {groupedLadders(dimension).map((group) => (
            <div key={group.names.join(',')} className="flex flex-col gap-1.5">
              {dimension.ladders.length > 1 && (
                <span className="text-[11px] text-[var(--ink-3)]">
                  {group.names.map(humanise).join(', ')}
                </span>
              )}
              <LadderRows tiers={group.tiers} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export function ApprovalLaddersSection({
  workflows,
  canEdit,
}: {
  workflows: ApprovalLadderWorkflow[]
  canEdit: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-[15px] font-medium text-[var(--ink)]">
          {t('claims.approvals.ladders.title', 'Levels of approval')}
        </h3>
        <p className="m-0 text-[12px] text-[var(--ink-3)]">
          {t(
            'claims.approvals.ladders.description',
            'A claim over any threshold waits for approval before it moves on. Each level is signed by the people in its role - manage them in Settings > Roles.',
          )}
        </p>
      </div>
      {workflows.map((workflow) => (
        <WorkflowLadders key={workflow.workflow_key} workflow={workflow} canEdit={canEdit} />
      ))}
    </div>
  )
}
